"use client";

import { useEffect, useId, useRef } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { ApiError } from "@/lib/api";
import { useExplainCode } from "../hooks";
import { playgroundErrorMessageKey } from "../lib/api-error-message";
import {
  createExplainOperationKey,
  shouldReuseExplainOperationKey,
} from "../lib/explain-operation";
import type { CodeExplainResult, ExplainCodeInput } from "../types";

type ExplainCodeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceCode: string;
  languageId: string;
};

export function ExplainCodeModal({
  open,
  onOpenChange,
  sourceCode,
  languageId,
}: ExplainCodeModalProps) {
  const t = useTranslations("playground");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const operationRef = useRef<ExplainCodeInput | null>(null);
  const headingId = useId();
  const mutation = useExplainCode();
  const data: CodeExplainResult | undefined = mutation.data;
  const errorKey = playgroundErrorMessageKey(mutation.error);
  const errorMessage = errorKey
    ? t(errorKey)
    : mutation.error instanceof ApiError
      ? mutation.error.message
      : null;

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open) {
      if (!el.open) el.showModal();
    } else if (el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      operationRef.current = null;
      return;
    }
    if (operationRef.current) return;
    mutation.reset();
    const code = sourceCode.trim();
    if (!code) return;
    const operation = {
      sourceCode: code,
      languageId,
      idempotencyKey: createExplainOperationKey(),
    };
    operationRef.current = operation;
    mutation.mutate(operation);
    // Intentionally when the dialog opens only — uses latest source from render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleRetry() {
    const previous = operationRef.current;
    if (!previous) return;
    const operation = shouldReuseExplainOperationKey(mutation.error)
      ? previous
      : { ...previous, idempotencyKey: createExplainOperationKey() };
    operationRef.current = operation;
    mutation.reset();
    mutation.mutate(operation);
  }

  function handleClose() {
    onOpenChange(false);
  }

  return (
    <dialog
      ref={dialogRef}
      className="w-[min(100vw-2rem,560px)] max-h-[min(90vh,720px)] rounded-xl border border-white/15 bg-[#141416] p-0 text-white shadow-2xl backdrop:bg-black/60"
      onClose={handleClose}
      aria-labelledby={headingId}
    >
      <div className="flex max-h-[min(90vh,720px)] flex-col gap-4 p-5">
        <div className="flex shrink-0 items-start justify-between gap-3">
          <h2 id={headingId} className="text-lg font-semibold text-white">
            {t("explainModalTitle")}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/85 transition hover:bg-white/10"
          >
            {t("explainModalClose")}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {mutation.isPending && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-white/55">
              <Loader2 className="h-8 w-8 animate-spin text-[#E94D1D]" aria-hidden />
              <span className="text-sm">{t("explainLoading")}</span>
            </div>
          )}

          {mutation.isError && !mutation.isPending && (
            <div
              role="alert"
              className="space-y-4 rounded-xl border border-red-400/25 bg-red-400/5 p-4"
            >
              <p className="text-sm text-red-300">{errorMessage ?? t("explainError")}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="min-h-11 rounded-lg border border-white/15 px-4 text-sm font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]"
              >
                {t("explainRetry")}
              </button>
            </div>
          )}

          {mutation.isSuccess && data && !mutation.isPending && (
            <div className="space-y-6 text-sm text-white/90">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {t("explainSummary")}
                </h3>
                <p className="whitespace-pre-wrap leading-relaxed text-white/85">{data.summary}</p>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {t("explainLineByLine")}
                </h3>
                <ul className="space-y-3">
                  {data.lineByLineBreakdown.map((row, i) => (
                    <li
                      key={`${row.lineRange}-${i}`}
                      className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
                    >
                      <p className="mb-1 font-mono text-xs text-[#FFAB8F]">
                        {t("explainLinesLabel", { range: row.lineRange })}
                      </p>
                      <p className="whitespace-pre-wrap text-white/80">{row.explanation}</p>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {t("explainImprovements")}
                </h3>
                <ul className="list-disc space-y-1.5 pl-5 text-white/85">
                  {data.improvements.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
